import { faCircleArrowUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, CardContent, Dialog, DialogActions, DialogContent, Stack, TextField, Typography } from "@mui/material";
import { VK_ENTER } from "app/appConstants";
import { MprcCard, MprcChipChoice, MprcClickable, MprcDialogTitleClose, MprcLink } from "components";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { TranscriptionContextualRAGResponse } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

////////////////////////////////////////////////////////////////////////////////

interface IPros {
  attachId: string;
  title: string;
  onClose: () => void;
}

export const TranscriptionAiSearchDialog = ({ attachId, title, onClose }: IPros) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [viewData, setViewData] = useState<TranscriptionContextualRAGResponse[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const newQuestionRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation("knowledge");
  const { enqueueSnackbar } = useSnackbar();

  const api = useCoreApi();

  const conversationId = useRef<string | null>(null);
  const canContinueSearch = useRef<boolean>(false);

  ///

  const ragSearch = async (question: string) => {
    setLoading(true);
    try {

      const response = await api.v1.knowledgePublication.transcriptionRagSearch({
        attachId,
        searchText: question,
        conversationId: conversationId.current
      });

      if (conversationId.current != null && conversationId.current != response.data.conversationId) {
        setViewData([response.data]);
        enqueueSnackbar(t("shared:GlobalSearch.RagSearch.ConversationExpiredText"), { variant: "warning" });
      }
      else {
        setViewData(state => {
          const newData = (state ?? []).filter(p => p.turnNumber != -1);
          return [...newData, response.data];
        });
      }

      canContinueSearch.current = response.data.errorCode == null || response.data.errorCode > -2;
      conversationId.current = response.data.conversationId;
    }
    catch (e) {
      MprcRequestSnackbarErrorReport(e);
    }
    finally {
      setLoading(false);
    }
  };

  ///

  const handleNewSearch = (newQuestion: string | null) => {
    canContinueSearch.current = true;
    conversationId.current = null;
    setViewData([]);
    if (newQuestion != null) newQuestionInContext(newQuestion);
  };

  ///

  const handleNewQuestionInContext = () => {
    if (newQuestionRef.current) {
      const question = newQuestionRef.current.value.trim();
      if (question != "") {
        if (canContinueSearch.current) newQuestionInContext(question);
        else handleNewSearch(question);

        newQuestionRef.current.value = "";
      }
    }
  };

  ///

  const newQuestionInContext = (question: string) => {
    setViewData(state => ([...state ?? [], {
      question,
      answer: "",
      sourcesUsed: [],
      relatedSearchSuggestions: [],
      contextUsed: "",
      processingTime: "",
      error: null,
      errorCode: null,
      metadata: null!,
      conversationId: "",
      turnNumber: -1,
      outOfContext: false,
      subjectMismatch: false,
      contextRelevance: -1,
      isFirstQuestion: false,
      conversationSummary: null,
      contextualMetadata: null!
    }]));

    ragSearch(question);
  };

  ///

  const RenderQuestion = (question: string) => {
    return (
      <Stack direction="row" justifyContent="end">
        <MprcCard paperColor="notice" elevation={0} sx={{ p: 1, border: 1, borderColor: "#fff2cc" }}>
          <Typography color="black">
            {question}
          </Typography>
        </MprcCard>
      </Stack>
    );
  };

  ///

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  ///

  const RenderNewConversation = () => {
    return (
      <Stack direction="column" gap={1} justifyContent="center" alignItems="center" height="100%">
        <img src="/img/app/RagSearch-NewSearch.gif" width={272} height={270}></img>
      </Stack>
    );
  };

  ///

  const RenderConversation = () => {
    if (viewData == null) return null;

    return (
      <Box className="ragsearch">
        {
          viewData.map((item, i) => {
            return (
              <Box id={`answer-{i}`} key={`answer-{i}`} mt={2}>

                {RenderQuestion(item.question)}

                {
                  item.turnNumber > -1 &&
                  <Box mt={1}>
                    {item.error
                      ? (
                        <MprcCard paperColor="error" elevation={1}>
                          <CardContent>
                            <Typography color="white">{item.error}</Typography>
                          </CardContent>
                        </MprcCard>
                      )
                      : (
                        <>
                          <div
                            className={item.outOfContext ? "subjectMismatch" : undefined}
                            dangerouslySetInnerHTML={{ __html: item.answer }}>
                          </div>

                          {!item.outOfContext &&
                            <Stack direction="row" gap={1} flexWrap="wrap">
                              {item.relatedSearchSuggestions.map((s, si) => (<MprcChipChoice key={si} label={s} title={s} onClick={() => newQuestionInContext(s)} />))}
                            </Stack>
                          }
                        </>
                      )
                    }
                  </Box>
                }
              </Box >
            );
          })
        }
      </Box>
    );
  };

  ///

  useEffect(() => {
    scrollToBottom();
    newQuestionRef.current?.focus();
  });

  ///

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <MprcDialogTitleClose
        title={t("Publication.TranscriptionSearchDlg.DialogTitle")}
        subtitle={title}
        onClose={onClose}
      />
      <DialogContent sx={{ height: "50vh" }}>
        {viewData.length == 0 &&
          RenderNewConversation()
        }

        {RenderConversation()}

        {loading && <img src="/img/app/RagSearch-Typing.gif" width={128} height={64}></img>}

        <div ref={messagesEndRef} />

      </DialogContent>
      <DialogActions>
        <Stack direction="column" gap={.5} mt={2} alignItems={"end"} width="100%">
          {
            (conversationId.current == null || (viewData.length != 0 && viewData[0].outOfContext == false)) &&
            <TextField
              autoFocus
              fullWidth
              inputRef={newQuestionRef}
              hiddenLabel
              disabled={loading}
              variant="outlined"
              autoComplete="off"
              autoCorrect="off"
              placeholder={conversationId.current == null ? t("Publication.TranscriptionSearchDlg.NewQuestionPlaceholder") : t("Publication.TranscriptionSearchDlg.ContinueSearchPlaceholder")}
              onKeyDown={(e) => e.key == VK_ENTER && handleNewQuestionInContext()}
              slotProps={
                {
                  htmlInput: {
                    sx: { p: 1 }
                  },
                  input: {
                    endAdornment: <MprcClickable onClick={handleNewQuestionInContext}><FontAwesomeIcon icon={faCircleArrowUp} /></MprcClickable>
                  }
                }
              }
            />
          }
          {
            viewData.length != 0 &&
            <MprcLink onClick={() => handleNewSearch(null)}>
              {t("Publication.TranscriptionSearchDlg.NewQuestion")}
            </MprcLink>
          }
        </Stack>
      </DialogActions>
    </Dialog >
  );
};